import { getRouteApi, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  investorProfileQueryOptions,
  useChangeSetItems,
  useNotifyChangeRequest,
} from "creso-core/hooks";
import {
  findPrimaryHolder,
  findSecondHolder,
  getInitials,
} from "creso-core/utils";
import {
  V2Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  Badge,
  ScrollArea,
  cn,
} from "web-design-system";
import { AccountChangesSummary, Avatar } from "web-core";
import { handleError } from "@/utils/error";
import { toast } from "sonner";
import omit from "lodash/omit";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { JOINT_CLIENT_HOLDING_PATTERNS } from "creso-core/constants";

function HolderApprovalRow({
  name,
  role,
  isApproved,
}: {
  name: string;
  role: string;
  isApproved?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <Avatar fallback={getInitials(name)} size="sm" />
        <div className="flex flex-col">
          <span className="text-sm font-semibold">{name}</span>
          <span className="text-xs text-muted-foreground">{role}</span>
        </div>
      </div>
      <Badge
        variant="custom"
        className={cn(
          "shrink-0",
          isApproved
            ? "bg-tag-green text-tag-green-foreground"
            : "bg-tag-yellow text-tag-yellow-foreground"
        )}
      >
        {isApproved ? "Approved" : "Approval pending"}
      </Badge>
    </div>
  );
}

export function NotifyUpdates() {
  const route = getRouteApi("/(auth)/_headed/clients/$id");
  const { client } = route.useLoaderData();
  const changes = useChangeSetItems(Number(client.id));

  const { mutateAsync: notifyChanges, isPending } = useNotifyChangeRequest();

  const isJointClient = Boolean(
    client.holding_pattern &&
    JOINT_CLIENT_HOLDING_PATTERNS.includes(client.holding_pattern)
  );

  const { data: holderProfiles } = useQuery(
    investorProfileQueryOptions(
      { client_id: client.id },
      { enabled: isJointClient }
    )
  );

  const primaryHolder = findPrimaryHolder(holderProfiles);
  const secondHolder = findSecondHolder(holderProfiles);

  const router = useRouter();

  const close = () => {
    if (router.history.canGoBack()) {
      router.history.back();
    } else {
      router.navigate({
        to: ".",
        search: (prev) => omit(prev, ["modal"]),
        replace: true,
      });
    }
  };

  if (!changes) return null;

  return (
    <V2Dialog
      defaultOpen
      onOpenChange={(open) => {
        if (!open) {
          close();
        }
      }}
    >
      <DialogContent className="overflow-hidden p-0 gap-0">
        <ScrollArea className="max-h-[55vh] p-6">
          <div className="flex flex-col flex-1">
            <DialogHeader>
              <DialogTitle className="!text-lg">Notify changes?</DialogTitle>
              <DialogDescription className="mt-3">
                {isJointClient
                  ? "Authorisation from both holders is required for the following edits:"
                  : `${client.name}'s authorisation is required for the following edits:`}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-5">
              <AccountChangesSummary
                client={client}
                holderProfiles={holderProfiles}
              />
            </div>
          </div>
        </ScrollArea>
        <div className="flex flex-col p-6 pt-0 bg-background">
          <span className="text-muted-foreground text-sm">
            Until authorised, all transactions will be made against previously
            saved information.
          </span>
          <ConditionalRenderer condition={isJointClient}>
            <div className="h-px w-full bg-border my-4" />
            <div className="flex flex-col gap-4">
              <HolderApprovalRow
                name={primaryHolder?.name ?? client.name}
                role="Primary holder"
                isApproved={primaryHolder?.is_approved}
              />
              <HolderApprovalRow
                name={secondHolder?.name ?? "Second holder"}
                role="Second holder"
                isApproved={secondHolder?.is_approved}
              />
            </div>
          </ConditionalRenderer>
          <div className="flex flex-row justify-end-safe gap-3 mt-4">
            <Button onClick={close} variant="outline">
              Cancel
            </Button>
            <Button
              disabled={isPending}
              onClick={() => {
                notifyChanges(client.id)
                  .then(() => toast.success("Notified client successfully"))
                  .then(close)
                  .catch((e) => handleError(e, "Failed to notify client"));
              }}
            >
              Yes, notify
            </Button>
          </div>
        </div>
      </DialogContent>
    </V2Dialog>
  );
}
